// Cloudflare Turnstile: the invisible check that starts a conversation (the
// API turns its token into a chat pass). The script loads only when a
// conversation needs a pass, never with the page.

interface TurnstileApi {
  render(container: HTMLElement, options: Record<string, unknown>): string;
  execute(container: HTMLElement | string): void;
  remove(widgetId: string): void;
}

declare global {
  interface Window {
    turnstile?: TurnstileApi;
  }
}

const SCRIPT = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";
const TIMEOUT_MS = 20_000;

let loading: Promise<TurnstileApi> | null = null;

function load(): Promise<TurnstileApi> {
  if (window.turnstile) return Promise.resolve(window.turnstile);
  loading ??= new Promise<TurnstileApi>((resolve, reject) => {
    const script = document.createElement("script");
    script.src = SCRIPT;
    script.async = true;
    script.onload = () => (window.turnstile ? resolve(window.turnstile) : reject(new Error("turnstile: no api")));
    script.onerror = () => {
      loading = null; // blocked or offline: the next attempt tries again
      script.remove();
      reject(new Error("turnstile: script blocked"));
    };
    document.head.append(script);
  });
  return loading;
}

/**
 * A token for this conversation: action and cData (the session id) come back
 * in the API's verification, so a token cannot be reused for another chat.
 */
export async function solveTurnstile(siteKey: string, action: string, sessionId: string): Promise<string> {
  const api = await load();
  // The widget is invisible; its container only has to exist.
  const box = document.createElement("div");
  box.setAttribute("aria-hidden", "true");
  box.style.cssText = "position:fixed;inset:auto auto 0 0;width:0;height:0;overflow:hidden";
  document.body.append(box);
  let widgetId: string | null = null;
  try {
    return await new Promise<string>((resolve, reject) => {
      const timer = setTimeout(() => reject(new Error("turnstile: timeout")), TIMEOUT_MS);
      const fail = (reason: string) => () => {
        clearTimeout(timer);
        reject(new Error(`turnstile: ${reason}`));
      };
      widgetId = api.render(box, {
        sitekey: siteKey,
        action,
        cData: sessionId,
        execution: "execute",
        callback: (token: string) => {
          clearTimeout(timer);
          resolve(token);
        },
        "error-callback": fail("error"),
        "timeout-callback": fail("challenge timeout"),
        "unsupported-callback": fail("unsupported browser"),
      });
      api.execute(box);
    });
  } finally {
    if (widgetId) {
      try {
        api.remove(widgetId);
      } catch {
        // already gone
      }
    }
    box.remove();
  }
}
